import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig, { getCustomSuffix, getSchemaId } from '../../config/gobodoConfig';
import type { CustomField } from '../queries/fieldQueries';
import type { CustomObject, PaginatedObjectsResult } from '../queries/objectQueries';
import { handleGobodoError, logGobodoError, ensureError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';

// ============================================
// KEY UTILITIES
// ============================================

/**
 * Converts CustomObject apiName to Gobodo key format
 * Removes custom suffix and converts to lowercase
 */
export function objectApiNameToKey(apiName: string): string {
  const suffix = getCustomSuffix();
  return apiName.replace(new RegExp(`${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '').toLowerCase();
}

/**
 * Converts objectId (e.g., "obj_client") to Gobodo key
 * Note: Now uses resolveObjectKey to return the full API name format (with __c)
 */
export function objectIdToKey(objectId: string): string {
  // Use resolveObjectKey to get the full API name format
  return resolveObjectKey(objectId);
}

/**
 * Converts Gobodo key back to objectId
 * Key now contains the full API name (e.g., "Product__c")
 */
export function keyToObjectId(key: string): string {
  // Remove custom suffix and convert to lowercase for the id prefix
  const suffix = getCustomSuffix();
  const suffixRegex = new RegExp(`${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i');
  const normalizedKey = key.replace(suffixRegex, '').toLowerCase();
  return `obj_${normalizedKey}`;
}

/**
 * Resolve objectId to Gobodo key for retrieval
 * 
 * IMPORTANT: Does NOT append custom suffix automatically. Uses the exact key format
 * as returned from the backend listing. This ensures retrieval uses the same
 * key format that was stored.
 * 
 * @param objectId - Either an objectId (e.g., "obj_product") or a key (e.g., "Product__c" or "product")
 * @param apiName - Optional: The exact apiName/key from the listing. If provided, use this instead.
 * @returns The Gobodo key exactly as stored (no modifications)
 */
export function resolveObjectKey(objectId: string, apiName?: string): string {
  // If apiName is provided (from listing/backend), use it as-is so Gobodo lookup matches exact key
  if (apiName) {
    return apiName;
  }
  
  const suffix = getCustomSuffix();
  
  // If objectId already contains the custom suffix or is already a key format, return it as-is
  if (objectId.includes(suffix) || !objectId.startsWith('obj_')) {
    return objectId;
  }
  
  // obj_case → case → normalize to Case__c via the standard normalizer
  const baseName = objectId.replace(/^obj_/, '');
  return normalizeToGobodoFormat(baseName);
}

// ============================================
// FIELD TYPE MAPPING
// ============================================

/**
 * Maps CustomField fieldType to Gobodo SkinField.TypeEnum
 */
function mapFieldTypeToGobodo(fieldType: CustomField['fieldType']): string {
  const typeMap: Record<string, string> = {
    text: 'text',
    textarea: 'textarea',
    longtextarea: 'textarea',
    richtextarea: 'wysiwyg',
    number: 'numeric',
    currency: 'currency',
    percent: 'percentage',
    date: 'date',
    datetime: 'datetime',
    checkbox: 'checkbox',
    picklist: 'select',
    multipicklist: 'multiple-select',
    lookup: 'relationship',
    masterdetail: 'relationship',
    email: 'email',
    phone: 'text',
    url: 'url',
    // Fallback for unsupported types
    autonumber: 'numeric',
    time: 'text',
    formula: 'text',
    rollup: 'numeric',
    geolocation: 'text',
    file: 'file',
    image: 'image',
  };

  return typeMap[fieldType] || 'text';
}

/**
 * Maps Gobodo SkinField.TypeEnum back to CustomField fieldType
 */
function mapGobodoTypeToFieldType(gobodoType: string): CustomField['fieldType'] {
  const typeMap: Record<string, CustomField['fieldType']> = {
    text: 'text',
    textarea: 'textarea',
    wysiwyg: 'richtextarea',
    'multiple-text': 'textarea',
    numeric: 'number',
    decimal: 'number',
    currency: 'currency',
    percentage: 'percent',
    url: 'url',
    email: 'email',
    select: 'picklist',
    'multiple-select': 'multipicklist',
    'enum-select': 'picklist',
    'enum-multiple-select': 'multipicklist',
    date: 'date',
    datetime: 'datetime',
    daterange: 'date',
    datetimerange: 'datetime',
    checkbox: 'checkbox',
    radiobutton: 'picklist',
    switch: 'checkbox',
    range: 'number',
    file: 'file',
    'multi-file': 'file',
    image: 'image',
    'multi-image': 'image',
    video: 'file',
    relationship: 'lookup',
    'relationship-multiple': 'lookup',
    'relationship-select': 'lookup',
    'relationship-multiple-select': 'lookup',
    'reverse-relationship': 'lookup',
    'relationship-or-text': 'lookup',
    object: 'lookup',
    'multi-object': 'lookup',
    user: 'lookup',
    'user-multiple': 'lookup',
    'user-select': 'lookup',
    'user-multiple-select': 'lookup',
  };

  return typeMap[gobodoType] || 'text';
}

// ============================================
// FIELD CONVERSION
// ============================================

/**
 * Converts CustomField to Gobodo SkinField
 * IMPORTANT: Keeps custom suffix in field name as Gobodo will store it with the suffix
 */
function convertToGobodoField(field: Omit<CustomField, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'isStandard'>): GobodoApi.SkinField {
  const gobodoField = new GobodoApi.SkinField();
  
  // Keep custom suffix - Gobodo will store field names with the suffix
  gobodoField.name = field.fieldName;
  gobodoField.label = field.fieldLabel;
  gobodoField.type = mapFieldTypeToGobodo(field.fieldType);
  
  // Combine description and helpText - helpText is user-facing help text
  // If both exist, combine them; otherwise use whichever is available
  if (field.helpText && field.description) {
    gobodoField.description = `${field.description}\n\nHelp Text: ${field.helpText}`;
  } else {
    gobodoField.description = field.helpText || field.description || '';
  }
  
  gobodoField.required = field.required;
  gobodoField.visible = field.isActive;
  gobodoField.searchable = true;
  
  if (field.precision !== undefined) {
    gobodoField.precision = field.precision;
  }
  
  if (field.fieldType === 'currency') {
    gobodoField.currencySymbol = '$';
  }
  
  // Gobodo expects options to be an array of strings for select/multiple-select fields
  if (field.picklistValues && field.picklistValues.length > 0) {
    gobodoField.options = field.picklistValues;
  }
  
  // For relationship fields, set objectSettings with isMasterDetail/isParentChild for Gobodo backend.
  // Cascade delete checked → isMasterDetail true. Cascade delete unchecked → isParentChild true (still master-detail in UI).
  // Omit objecttypeid when we don't have a value: sending 0 can cause the API to overwrite
  // the field's owning objectTypeId with 0, breaking getFieldById/update flows.
  if (field.fieldType === 'lookup' || field.fieldType === 'masterdetail') {
    const isMasterDetailField = field.fieldType === 'masterdetail';
    const cascadeDelete = isMasterDetailField && (field.masterDetailCascadeDelete !== false);
    const objectSettings: {
      relationshiptypeid: number;
      isMasterDetail: boolean;
      isParentChild: boolean;
      objecttypeid?: number;
    } = {
      relationshiptypeid: field.relTypeId ?? 0,
      isMasterDetail: cascadeDelete,
      isParentChild: isMasterDetailField && !cascadeDelete,
    };
    const targetObjectTypeId = (field as any).targetObjectTypeId as number | undefined;
    if (targetObjectTypeId != null && targetObjectTypeId !== 0) {
      objectSettings.objecttypeid = targetObjectTypeId;
    }
    (gobodoField as any).objectSettings = objectSettings;
  }
  
  return gobodoField;
}

/**
 * Converts Gobodo SkinFieldResponse to CustomField
 * IMPORTANT: Preserves field name exactly as stored in Gobodo (may or may not have custom suffix)
 */
function convertFromGobodoField(
  gobodoField: any,
  objectKey: string
): CustomField {
  const objectId = keyToObjectId(objectKey);
  
  // Extract picklist values: Gobodo SkinFieldResponse has options/enumValues as Array<string>; support legacy shapes too
  let picklistValues: string[] = [];
  const rawOptions =
    gobodoField.options ??
    (typeof gobodoField.getOptions === 'function' ? gobodoField.getOptions() : undefined);
  const rawEnumValues =
    gobodoField.enumValues ??
    (typeof gobodoField.getEnumValues === 'function' ? gobodoField.getEnumValues() : undefined);
  if (Array.isArray(rawOptions) && rawOptions.length > 0) {
    picklistValues = rawOptions.map((v: any) => (typeof v === 'string' ? v : v?.value ?? v?.label ?? '')).filter(Boolean);
  } else if (Array.isArray(rawEnumValues) && rawEnumValues.length > 0) {
    picklistValues = rawEnumValues.map((v: any) => (typeof v === 'string' ? v : v?.value ?? v?.label ?? '')).filter(Boolean);
  } else if (rawOptions && Array.isArray((rawOptions as any)?.values)) {
    picklistValues = (rawOptions as any).values.map((v: any) => (typeof v === 'string' ? v : v?.value ?? v?.label ?? '')).filter(Boolean);
  }
  
  // Preserve field name exactly as stored in Gobodo (with or without custom suffix)
  const fieldName = gobodoField.name || '';
  
  // Map field type and log for debugging
  const gobodoType = gobodoField.type || '';
  let mappedType = mapGobodoTypeToFieldType(gobodoType);
  // Read back master-detail from objectSettings (or top-level field) when isMasterDetail or isParentChild is set.
  // List endpoint may return objectSettings in different shapes or omit it; support all variants.
  const raw = gobodoField as Record<string, unknown>;
  const objectSettings = gobodoField.objectSettings ?? raw.object_settings;
  const os = objectSettings && typeof objectSettings === 'object' ? (objectSettings as Record<string, unknown>) : undefined;
  const readBool = (obj: Record<string, unknown> | undefined, ...keys: string[]): boolean => {
    if (!obj) return false;
    for (const k of keys) {
      if (obj[k] === true) return true;
    }
    return false;
  };
  const isMasterDetail = !!(
    readBool(os, 'isMasterDetail', 'ismasterdetail', 'masterDetail', 'is_master_detail')
    || readBool(raw, 'isMasterDetail', 'ismasterdetail', 'masterDetail', 'is_master_detail')
    || (typeof (objectSettings as any)?.getIsMasterDetail === 'function' && (objectSettings as any).getIsMasterDetail())
  );
  const isParentChild = !!(
    readBool(os, 'isParentChild', 'isparentchild', 'parentChild', 'is_parent_child')
    || readBool(raw, 'isParentChild', 'isparentchild', 'parentChild', 'is_parent_child')
    || (typeof (objectSettings as any)?.getIsParentChild === 'function' && (objectSettings as any).getIsParentChild())
  );
  if (mappedType === 'lookup' && (isMasterDetail || isParentChild)) {
    mappedType = 'masterdetail';
  }
  const masterDetailCascadeDelete = isMasterDetail;
  
  // Debug logging for rich text fields
  if (gobodoType === 'wysiwyg' || mappedType === 'richtextarea' || fieldName.toLowerCase().includes('rich')) {
    console.log(`[convertFromGobodoField] Rich text field conversion:`, {
      fieldName,
      gobodoType,
      mappedType,
      originalField: gobodoField
    });
  }
  
  // Extract relTypeId for relationship fields (lookup/masterdetail)
  // This will be used to fetch the relationship type and determine end object types for search
  const relTypeId = gobodoField.relTypeId;
  
  // Extract defaultValue (Gobodo SkinFieldResponse has defaultValue; used for picklist default selection)
  const defaultValue =
    gobodoField.defaultValue ??
    (typeof gobodoField.getDefaultValue === 'function' ? gobodoField.getDefaultValue() : undefined);
  const normalizedDefault =
    defaultValue !== undefined && defaultValue !== null && defaultValue !== ''
      ? String(defaultValue)
      : undefined;
  
  return {
    id: `field_${gobodoField.id}`,
    objectId,
    fieldName: fieldName, // Preserve exact field name from Gobodo (may include custom suffix or not)
    fieldLabel: gobodoField.label || '',
    fieldType: mappedType as CustomField['fieldType'],
    description: gobodoField.description,
    required: gobodoField.required || false,
    unique: false,
    isPII: false,
    isStandard: false,
    isActive: gobodoField.visible !== false,
    fieldLevelSecurity: {
      visibleTo: ['all'],
      editableBy: ['admin']
    },
    createdBy: 'System',
    createdDate: new Date().toISOString(),
    lastModified: new Date().toISOString(),
    picklistValues,
    defaultValue: normalizedDefault,
    precision: gobodoField.precision,
    length: undefined,
    scale: undefined,
    relTypeId, // Store relationship type ID for lookup fields
    masterDetailCascadeDelete: mappedType === 'masterdetail' ? masterDetailCascadeDelete : undefined,
  };
}

// ============================================
// OBJECT TYPE CONVERSION
// ============================================

/**
 * Converts Gobodo GraphObjectTypeResponse to CustomObject
 */
function convertFromGobodoObjectType(gobodoType: any): CustomObject {
  // Extract icon from properties field, fallback to 'Database' if not found
  const icon = gobodoType.properties?.icon || 'Database';
  
  // Key and name now contain the full API name (with custom suffix)
  // IMPORTANT: Preserve the exact key format as returned from backend (case-sensitive)
  const apiName = gobodoType.key || gobodoType.name;
  
  // Verbose object type conversion logging gated behind debug flag
  if ((typeof window !== 'undefined' && (window as any).__GOBODO_DEBUG_RECORDS__)) {
    console.log('[Gobodo] Converting object type:', { gobodoKey: gobodoType.key, apiName });
  }
  
  return {
    id: keyToObjectId(apiName),
    gobodoTypeId: gobodoType.id,
    label: gobodoType.singularname || gobodoType.name,
    apiName: apiName, // Preserve exact key format from backend (no modifications)
    pluralLabel: gobodoType.pluralname || gobodoType.name,
    description: gobodoType.description || '',
    icon: icon,
    isCustom: true,
    isActive: true,
    // Use fieldCount from API if available, otherwise fallback to calculating from fields array
    fieldCount: gobodoType.fieldCount ?? gobodoType.fields?.length ?? gobodoType.skinFields?.length ?? 0,
    recordCount: 0,
    lastModified: gobodoType.modificationdate?.toISOString() || new Date().toISOString(),
    createdBy: 'System',
    category: 'custom'
  };
}

// ============================================
// OBJECT TYPE OPERATIONS
// ============================================

/**
 * Create a new object type in Gobodo and add it to schema
 */
export async function createGobodoObjectType(
  objectData: Omit<CustomObject, 'id' | 'fieldCount' | 'recordCount' | 'lastModified' | 'createdBy'>
): Promise<CustomObject> {
  try {
    const client = getGobodoClient();
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
    const schemasApi = new GobodoApi.GraphSchemasApi(client);
    
    // Use the full API name (with custom suffix) for both key and name
    const apiName = objectData.apiName;
    
    const input = new GobodoApi.GraphObjectTypeInput([], [], {});
    input.name = apiName;
    input.key = apiName;
    input.singularname = objectData.label;
    input.pluralname = objectData.pluralLabel;
    input.description = objectData.description;
    input.searchable = true;
    input.abstract = false;
    const category: 'core' | 'system' | 'custom' = objectData.category || 'custom';
    const schemaId = getSchemaId(category);
    input.schemaid = schemaId;
    input.fields = [];
    input.methods = [];
    input.properties = {
      icon: objectData.icon || 'Package'
    };
    
    // Create object type with retry
    const response = await retryWithBackoff(
      () => objectTypesApi.createObjectType(input),
      { maxRetries: 2 }
    );
    
    // Add object type to schema with retry
    await retryWithBackoff(
      () => schemasApi.addSchemaObjectTypes(schemaId, [response.id]),
      { maxRetries: 2 }
    );
    
    return convertFromGobodoObjectType(response);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'createGobodoObjectType', objectType: objectData.apiName });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Update an existing object type in Gobodo
 */
export async function updateGobodoObjectType(
  objectId: string,
  updates: Partial<CustomObject>
): Promise<CustomObject> {
  try {
    const client = getGobodoClient();
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

    // Resolve objectId to Gobodo key and fetch existing type for its numeric ID
    const key = resolveObjectKey(objectId);
    const existing = await retryWithBackoff(
      () => objectTypesApi.getObjectTypeByKey(key, { maxFields: 0 }),
      { maxRetries: 2 }
    );
    const numericId = existing.id as number;

    const input = new GobodoApi.GraphObjectTypeInput([], [], {});
    input.name = existing.key || existing.name;
    input.key = existing.key || existing.name;
    input.singularname = updates.label ?? existing.singularname;
    input.pluralname = updates.pluralLabel ?? existing.pluralname;
    input.description = updates.description ?? existing.description;
    input.searchable = true;
    input.abstract = false;
    input.schemaid = existing.schemaid;
    input.fields = [];
    input.methods = [];
    input.properties = {
      ...(existing.properties || {}),
      icon: updates.icon ?? existing.properties?.icon ?? 'Package'
    };

    const response = await retryWithBackoff(
      () => objectTypesApi.updateObjectType(numericId, input),
      { maxRetries: 2 }
    );

    return convertFromGobodoObjectType(response);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'updateGobodoObjectType', objectId });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Delete (remove) an object type from Gobodo
 */
export async function deleteGobodoObjectType(objectId: string): Promise<void> {
  try {
    const client = getGobodoClient();
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

    const key = resolveObjectKey(objectId);
    const existing = await retryWithBackoff(
      () => objectTypesApi.getObjectTypeByKey(key, { maxFields: 0 }),
      { maxRetries: 2 }
    );
    const numericId = existing.id as number;

    await retryWithBackoff(
      () => objectTypesApi.deleteObjectType(numericId),
      { maxRetries: 2 }
    );
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'deleteGobodoObjectType', objectId });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

async function fetchRecordCountForObjectType(objectKey: string): Promise<number | undefined> {
  try {
    const client = getGobodoClient();
    const objectsApi = new GobodoApi.GraphObjectsApi(client);
    const graphId = GobodoConfig.graph();
    // Fetch minimal page (size=1) to check if records exist, then estimate count.
    // Previously fetched size=5000 which caused multi-second timeouts for large objects.
    const firstPage = await retryWithBackoff(
      () => objectsApi.getObjectsInGraphByType(graphId, objectKey, { page: 0, size: 1 }),
      { maxRetries: 1 }
    );
    if (!Array.isArray(firstPage) || firstPage.length === 0) return 0;
    // Gobodo doesn't expose a count endpoint, so return a conservative indicator.
    // A value of 1 means "records exist" — exact count requires full scan.
    return firstPage.length;
  } catch {
    // Return undefined to signal "count unavailable" (not "zero records")
    return undefined;
  }
}

/**
 * Internal: get object type by key without queue. Use only when caller already holds queue slot
 * (e.g. ensureObjectTypeExists called from createGobodoField/createGobodoRecord).
 */
export async function getGobodoObjectTypeByKeyInternal(key: string, includeFields: boolean = false): Promise<CustomObject> {
  const client = getGobodoClient();
  const api = new GobodoApi.GraphObjectTypesApi(client);
  // Fetch object metadata and record count in parallel (not sequentially)
  const [response, recordCount] = await Promise.all([
    retryWithBackoff(
      () => api.getObjectTypeByKey(key, includeFields ? {} : { maxFields: 0 }),
      { maxRetries: 5, initialDelayMs: 2000 }
    ),
    fetchRecordCountForObjectType(key),
  ]);
  const obj = convertFromGobodoObjectType(response);
  return { ...obj, recordCount: recordCount ?? obj.recordCount };
}

/**
 * Get object type by key
 * @param key - Object type key
 * @param includeFields - Whether to include fields in response (default: false)
 */
export async function getGobodoObjectTypeByKey(key: string, includeFields: boolean = false): Promise<CustomObject> {
  try {
    return await getGobodoObjectTypeByKeyInternal(key, includeFields);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'getGobodoObjectTypeByKey', objectType: key });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Resolve object type by key from schema lists when getObjectTypeByKey returns 500.
 * Returns raw API shape { id, key, name, singularname } for use in relationship APIs.
 */
export async function getObjectTypeByKeyFromSchema(key: string): Promise<{ id: number; key: string; name: string; singularname?: string } | null> {
  const client = getGobodoClient();
  const schemasApi = new GobodoApi.GraphSchemasApi(client);
  const coreId = GobodoConfig.schemaCore();
  const systemId = GobodoConfig.schemaSystem();
  const customId = GobodoConfig.schemaCustom();
  const keyLower = key.toLowerCase();

  // Fetch schemas in parallel - no retries since we gracefully handle failures
  const fetchSchema = async (schemaId: number) => {
    try {
      return await schemasApi.getSchemaObjectTypes(schemaId, { maxFields: 0 });
    } catch { return [] as any[]; }
  };
  const [core, system, custom] = await Promise.all([
    fetchSchema(coreId),
    fetchSchema(systemId),
    fetchSchema(customId),
  ]);

  const all = [...(core || []), ...(system || []), ...(custom || [])];
  const found = all.find((t: any) => (t.key || t.name || '').toLowerCase() === keyLower);
  return found ? { id: found.id, key: found.key || found.name, name: found.name || found.key, singularname: found.singularname } : null;
}

/**
 * Get object type by key; on 500 from getObjectTypeByKey, resolve from schema list.
 * Returns raw shape { id, key, name, singularname } for relationship/create usage.
 */
export async function getObjectTypeByKeyWithFallback(key: string): Promise<{ id: number; key: string; name: string; singularname?: string }> {
  const client = getGobodoClient();
  const api = new GobodoApi.GraphObjectTypesApi(client);
  try {
    const response = await retryWithBackoff(
      () => api.getObjectTypeByKey(key, { maxFields: 0 }),
      { maxRetries: 2 }
    );
    return { id: response.id, key: response.key || response.name, name: response.name || response.key, singularname: response.singularname };
  } catch {
    const fromSchema = await getObjectTypeByKeyFromSchema(key);
    if (fromSchema) return fromSchema;
    throw new Error(`Object type not found: ${key}`);
  }
}

/**
 * Get object types from a specific schema by category
 * @param category - Object category: 'core', 'system', or 'custom'
 * @returns Array of CustomObject from the specified schema
 */
export async function getGobodoObjectTypesByCategory(category: 'core' | 'system' | 'custom'): Promise<CustomObject[]> {
  const client = getGobodoClient();
  const schemasApi = new GobodoApi.GraphSchemasApi(client);
  
  const schemaId = getSchemaId(category);
  
  let objectTypes: any[];
  try {
    objectTypes = await retryWithBackoff(
      () => schemasApi.getSchemaObjectTypes(schemaId, { maxFields: 0 }),
      { maxRetries: 5, initialDelayMs: 2000 }
    );
  } catch (error: any) {
    const status = error?.status ?? error?.response?.status;
    if (status === 404) return [];
    throw ensureError(error);
  }
  
  if (!objectTypes || objectTypes.length === 0) {
    return [];
  }
  
  return objectTypes.map((type: any) => {
    const converted = convertFromGobodoObjectType(type);
    return { ...converted, category };
  });
}

function readHeaderNumber(headers: Record<string, any> | undefined, keys: string[]): number | undefined {
  if (!headers) return undefined;

  for (const [headerKey, headerValue] of Object.entries(headers)) {
    if (!keys.includes(headerKey.toLowerCase())) continue;

    const rawValue = Array.isArray(headerValue) ? headerValue[0] : headerValue;
    const parsed = Number(rawValue);
    if (Number.isFinite(parsed)) return parsed;
  }

  return undefined;
}

function buildPagedObjectResult(
  items: CustomObject[],
  apiPage: number,
  pageSize: number,
  headers?: Record<string, any>
): PaginatedObjectsResult {
  const totalRecordsFromHeaders = readHeaderNumber(headers, [
    'x-total-count',
    'total-count',
    'x-total',
    'total',
    'count',
  ]);
  const totalPagesFromHeaders = readHeaderNumber(headers, [
    'x-total-pages',
    'total-pages',
    'totalpages',
    'page-count',
  ]);

  const hasPreviousPage = apiPage > 0;
  const inferredHasNextPage =
    totalPagesFromHeaders != null
      ? apiPage + 1 < totalPagesFromHeaders
      : items.length === pageSize;
  const totalPages =
    totalPagesFromHeaders ??
    (totalRecordsFromHeaders != null
      ? Math.max(1, Math.ceil(totalRecordsFromHeaders / pageSize))
      : apiPage + 1 + (inferredHasNextPage ? 1 : 0));
  const totalRecords =
    totalRecordsFromHeaders ??
    (inferredHasNextPage
      ? (apiPage + 1) * pageSize + 1
      : apiPage * pageSize + items.length);

  return {
    items,
    page: apiPage + 1,
    pageSize,
    totalRecords,
    totalPages,
    hasNextPage: inferredHasNextPage,
    hasPreviousPage,
    isTotalExact: totalRecordsFromHeaders != null || totalPagesFromHeaders != null,
  };
}

let objectTypeCategoryMapCache: Map<string, 'core' | 'system' | 'custom'> | null = null;
let objectTypeCategoryMapPromise: Promise<Map<string, 'core' | 'system' | 'custom'>> | null = null;

async function loadObjectTypeCategoryMap(): Promise<Map<string, 'core' | 'system' | 'custom'>> {
  const [coreTypes, systemTypes, customTypes] = await Promise.all([
    getGobodoObjectTypesByCategory('core'),
    getGobodoObjectTypesByCategory('system'),
    getGobodoObjectTypesByCategory('custom'),
  ]);

  const categoryByKey = new Map<string, 'core' | 'system' | 'custom'>();
  for (const objectType of coreTypes) categoryByKey.set(objectType.apiName.toLowerCase(), 'core');
  for (const objectType of systemTypes) categoryByKey.set(objectType.apiName.toLowerCase(), 'system');
  for (const objectType of customTypes) categoryByKey.set(objectType.apiName.toLowerCase(), 'custom');
  return categoryByKey;
}

function getCachedObjectTypeCategoryMap(): Map<string, 'core' | 'system' | 'custom'> | null {
  return objectTypeCategoryMapCache;
}

function primeObjectTypeCategoryMap(): Promise<Map<string, 'core' | 'system' | 'custom'>> {
  if (objectTypeCategoryMapCache) {
    return Promise.resolve(objectTypeCategoryMapCache);
  }

  if (!objectTypeCategoryMapPromise) {
    objectTypeCategoryMapPromise = loadObjectTypeCategoryMap()
      .then((map) => {
        objectTypeCategoryMapCache = map;
        objectTypeCategoryMapPromise = null;
        return map;
      })
      .catch((error) => {
        objectTypeCategoryMapPromise = null;
        throw error;
      });
  }

  return objectTypeCategoryMapPromise;
}

const RELATIONSHIP_TYPES_PAGE_SIZE = 200;
let relationshipTypeByIdCache: Map<number, any> | null = null;
let relationshipTypeByIdPromise: Promise<Map<number, any>> | null = null;
const relationshipTypeByIdFallbackCache = new Map<number, any>();
const relationshipTypeByIdFallbackPromiseCache = new Map<number, Promise<any | null>>();

async function loadRelationshipTypeMap(): Promise<Map<number, any>> {
  const client = getGobodoClient();
  const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
  const relationshipById = new Map<number, any>();
  let page = 0;

  while (true) {
    const currentPage = page;
    const batch = await retryWithBackoff(
      () => relationshipTypesApi.getRelationshipTypesInGraph(GobodoConfig.graph(), {
        page: currentPage,
        size: RELATIONSHIP_TYPES_PAGE_SIZE,
      }),
      { maxRetries: 2 }
    );

    if (!Array.isArray(batch) || batch.length === 0) {
      break;
    }

    for (const relationshipType of batch) {
      const relationshipTypeId = Number(relationshipType?.id);
      if (Number.isFinite(relationshipTypeId)) {
        relationshipById.set(relationshipTypeId, relationshipType);
      }
    }

    if (batch.length < RELATIONSHIP_TYPES_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return relationshipById;
}

function primeRelationshipTypeMap(): Promise<Map<number, any>> {
  if (relationshipTypeByIdCache) {
    return Promise.resolve(relationshipTypeByIdCache);
  }

  if (!relationshipTypeByIdPromise) {
    relationshipTypeByIdPromise = loadRelationshipTypeMap()
      .then((map) => {
        relationshipTypeByIdCache = map;
        map.forEach((relationshipType, relationshipTypeId) => {
          relationshipTypeByIdFallbackCache.set(relationshipTypeId, relationshipType);
        });
        relationshipTypeByIdPromise = null;
        return map;
      })
      .catch((error) => {
        relationshipTypeByIdPromise = null;
        throw error;
      });
  }

  return relationshipTypeByIdPromise;
}

async function getRelationshipTypeByIdCached(
  relationshipTypeId: number,
  relationshipTypesApi: InstanceType<typeof GobodoApi.GraphRelationshipTypesApi>
): Promise<any | null> {
  if (relationshipTypeByIdFallbackCache.has(relationshipTypeId)) {
    return relationshipTypeByIdFallbackCache.get(relationshipTypeId) ?? null;
  }

  const existingPromise = relationshipTypeByIdFallbackPromiseCache.get(relationshipTypeId);
  if (existingPromise) {
    return existingPromise;
  }

  const fetchPromise = retryWithBackoff(
    () => relationshipTypesApi.getRelationshipTypeById(relationshipTypeId),
    { maxRetries: 2 }
  )
    .then((relationshipType) => {
      relationshipTypeByIdFallbackCache.set(relationshipTypeId, relationshipType);
      relationshipTypeByIdFallbackPromiseCache.delete(relationshipTypeId);
      return relationshipType;
    })
    .catch((error) => {
      relationshipTypeByIdFallbackPromiseCache.delete(relationshipTypeId);
      throw error;
    });

  relationshipTypeByIdFallbackPromiseCache.set(relationshipTypeId, fetchPromise);
  return fetchPromise;
}

function applyRelationshipTypeMetadataToField(field: CustomField, relationshipType: any): CustomField {
  const rel = relationshipType as Record<string, any>;

  if (!field.lookupObject) {
    const endObjectType = relationshipType?.endObjectTypes?.[0];
    if (endObjectType?.key) {
      field.lookupObject = endObjectType.key;
    }
  }

  const relIsMasterDetail = rel?.ismasterdetail === true || rel?.isMasterDetail === true;
  const relIsParentChild =
    rel?.isparentchild === true ||
    rel?.isParentChildRelationship === true ||
    rel?.isParentChild === true;

  if (field.fieldType === 'lookup' && (relIsMasterDetail || relIsParentChild)) {
    field.fieldType = 'masterdetail';
    field.masterDetailCascadeDelete = relIsMasterDetail;
  }

  return field;
}

async function enrichRelationshipFields(
  fields: CustomField[],
  client: ReturnType<typeof getGobodoClient>
): Promise<CustomField[]> {
  const relationshipTypeIds = Array.from(
    new Set(
      fields
        .filter((field) => (field.fieldType === 'lookup' || field.fieldType === 'masterdetail') && field.relTypeId)
        .map((field) => Number(field.relTypeId))
        .filter((relationshipTypeId) => Number.isFinite(relationshipTypeId) && relationshipTypeId > 0)
    )
  );

  if (relationshipTypeIds.length === 0) {
    return fields;
  }

  try {
    await primeRelationshipTypeMap();
  } catch (error) {
    console.warn('[getGobodoFields] Failed to preload relationship types from graph, falling back to targeted fetches:', error);
  }

  const relationshipTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
  const relationshipTypesById = new Map<number, any>();
  const missingRelationshipTypeIds = relationshipTypeIds.filter(
    (relationshipTypeId) => !relationshipTypeByIdFallbackCache.has(relationshipTypeId)
  );

  if (missingRelationshipTypeIds.length > 0) {
    await Promise.all(
      missingRelationshipTypeIds.map(async (relationshipTypeId) => {
        try {
          const relationshipType = await getRelationshipTypeByIdCached(relationshipTypeId, relationshipTypesApi);
          if (relationshipType) {
            relationshipTypesById.set(relationshipTypeId, relationshipType);
          }
        } catch (error) {
          console.warn(`[getGobodoFields] Failed to fetch relationship type ${relationshipTypeId}:`, error);
        }
      })
    );
  }

  for (const relationshipTypeId of relationshipTypeIds) {
    const cachedRelationshipType = relationshipTypeByIdFallbackCache.get(relationshipTypeId);
    if (cachedRelationshipType) {
      relationshipTypesById.set(relationshipTypeId, cachedRelationshipType);
    }
  }

  return fields.map((field) => {
    if (!field.relTypeId || (field.fieldType !== 'lookup' && field.fieldType !== 'masterdetail')) {
      return field;
    }

    const relationshipType = relationshipTypesById.get(Number(field.relTypeId));
    if (!relationshipType) {
      return field;
    }

    return applyRelationshipTypeMetadataToField(field, relationshipType);
  });
}

export async function getGobodoObjectTypesPageByCategory(
  category: 'core' | 'system' | 'custom',
  options: { page: number; size: number }
): Promise<PaginatedObjectsResult> {
  const client = getGobodoClient();
  const schemasApi = new GobodoApi.GraphSchemasApi(client);

  const schemaId = getSchemaId(category);
  const page = Math.max(options.page, 0);
  const size = Math.min(Math.max(options.size, 1), 100);

  try {
    const response = await retryWithBackoff(
      () => schemasApi.getSchemaObjectTypesWithHttpInfo(schemaId, { page, size, maxFields: 0 }),
      { maxRetries: 5, initialDelayMs: 2000 }
    );

    const objectTypes = Array.isArray(response?.data) ? response.data : [];
    const items = objectTypes.map((type: any) => ({
      ...convertFromGobodoObjectType(type),
      category,
    }));

    return buildPagedObjectResult(items, page, size, response?.response?.headers);
  } catch (error: any) {
    const status = error?.status ?? error?.response?.status;
    if (status === 404) {
      return {
        items: [],
        page: page + 1,
        pageSize: size,
        totalRecords: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: page > 0,
        isTotalExact: true,
      };
    }
    throw ensureError(error);
  }
}

export async function getGobodoObjectTypesPageInGraph(
  options: { page: number; size: number }
): Promise<PaginatedObjectsResult> {
  const client = getGobodoClient();
  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  const graphId = GobodoConfig.graph();
  const page = Math.max(options.page, 0);
  const size = Math.min(Math.max(options.size, 1), 100);

  try {
    const response = await retryWithBackoff(
      () => objectTypesApi.getObjectTypesInGraphWithHttpInfo(graphId, { page, size }),
      { maxRetries: 3, initialDelayMs: 1000 }
    );

    const objectTypes = Array.isArray(response?.data) ? response.data : [];
    const categoryByKey = getCachedObjectTypeCategoryMap();
    if (!categoryByKey) {
      void primeObjectTypeCategoryMap().catch(() => {
        // Best-effort enrichment only; graph paging should not wait on schema calls.
      });
    }

    const items = objectTypes.map((type: any) => {
      const converted = convertFromGobodoObjectType(type);
      const category = categoryByKey?.get(String(converted.apiName).toLowerCase()) ?? 'custom';
      return { ...converted, category };
    });

    return buildPagedObjectResult(items, page, size, response?.response?.headers);
  } catch (error: any) {
    const status = error?.status ?? error?.response?.status;
    if (status === 404) {
      return {
        items: [],
        page: page + 1,
        pageSize: size,
        totalRecords: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: page > 0,
        isTotalExact: true,
      };
    }
    throw ensureError(error);
  }
}

export async function searchGobodoObjectTypes(
  query: string,
  options: { page: number; size: number; category?: 'core' | 'system' | 'custom' }
): Promise<PaginatedObjectsResult> {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    return {
      items: [],
      page: Math.max(options.page, 0) + 1,
      pageSize: Math.min(Math.max(options.size, 1), 100),
      totalRecords: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: Math.max(options.page, 0) > 0,
      isTotalExact: true,
    };
  }

  const client = getGobodoClient();
  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  const page = Math.max(options.page, 0);
  const size = Math.min(Math.max(options.size, 1), 100);
  const schemaId = options.category ? getSchemaId(options.category) : undefined;
  const graphId = options.category ? undefined : GobodoConfig.graph();

  try {
    const response = await retryWithBackoff(
      () => objectTypesApi.searchObjectTypesWithHttpInfo(normalizedQuery, {
        start: page * size,
        max: size,
        schemaid: schemaId,
        graphid: graphId,
      }),
      { maxRetries: 5, initialDelayMs: 2000 }
    );

    const objectTypes = Array.isArray(response?.data) ? response.data : [];
    const categoryByKey = options.category ? undefined : getCachedObjectTypeCategoryMap();
    if (!options.category && !categoryByKey) {
      void primeObjectTypeCategoryMap().catch(() => {
        // Best-effort enrichment only; search results should still render without it.
      });
    }

    const items = objectTypes.map((type: any) => {
      const converted = convertFromGobodoObjectType(type);
      const category =
        options.category ??
        categoryByKey?.get(String(converted.apiName).toLowerCase()) ??
        'custom';

      return {
        ...converted,
        category,
      };
    });

    return buildPagedObjectResult(items, page, size, response?.response?.headers);
  } catch (error: any) {
    const status = error?.status ?? error?.response?.status;
    if (status === 404) {
      return {
        items: [],
        page: page + 1,
        pageSize: size,
        totalRecords: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: page > 0,
        isTotalExact: true,
      };
    }
    throw ensureError(error);
  }
}

/**
 * Get all object types in the active graph.
 * Uses the graph-level endpoint for the master list, then enriches categories
 * from schema membership so the UI can still distinguish core/system/custom.
 */
export async function getGobodoObjectTypes(): Promise<CustomObject[]> {
  const client = getGobodoClient();
  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  const graphId = GobodoConfig.graph();

  try {
    const graphTypes = await retryWithBackoff(
      () => objectTypesApi.getObjectTypesInGraph(graphId),
      { maxRetries: 3, initialDelayMs: 1000 }
    );
    const categoryByKey = getCachedObjectTypeCategoryMap();
    if (!categoryByKey) {
      void primeObjectTypeCategoryMap().catch(() => {
        // Best-effort enrichment only; the graph list should not wait on schema calls.
      });
    }

    if (Array.isArray(graphTypes) && graphTypes.length > 0) {
      return graphTypes.map((type: any) => {
        const converted = convertFromGobodoObjectType(type);
        const category = categoryByKey?.get(String(converted.apiName).toLowerCase()) ?? 'custom';
        return { ...converted, category };
      });
    }
  } catch {
    // Fall through to schema-based fallback if the graph endpoint is unavailable
  }

  const schemasApi = new GobodoApi.GraphSchemasApi(client);
  const coreSchemaId = GobodoConfig.schemaCore();
  const systemSchemaId = GobodoConfig.schemaSystem();
  const customSchemaId = GobodoConfig.schemaCustom();

  const fetchSchema = async (schemaId: number): Promise<any[]> => {
    try {
      return await schemasApi.getSchemaObjectTypes(schemaId, { maxFields: 0 });
    } catch {
      return [];
    }
  };
  const [coreTypes, systemTypes, customTypes] = await Promise.all([
    fetchSchema(coreSchemaId),
    fetchSchema(systemSchemaId),
    fetchSchema(customSchemaId),
  ]);

  const allTypes: CustomObject[] = [];
  if (coreTypes.length > 0) {
    allTypes.push(...coreTypes.map((type: any) => ({ ...convertFromGobodoObjectType(type), category: 'core' as const })));
  }
  if (systemTypes.length > 0) {
    allTypes.push(...systemTypes.map((type: any) => ({ ...convertFromGobodoObjectType(type), category: 'system' as const })));
  }
  if (customTypes.length > 0) {
    allTypes.push(...customTypes.map((type: any) => ({ ...convertFromGobodoObjectType(type), category: 'custom' as const })));
  }

  if (allTypes.length > 0) {
    return allTypes;
  }

  // ── Fallback: schema endpoints broken, discover types individually ──
  console.warn('[getGobodoObjectTypes] Schema listing failed for all schemas, falling back to individual type lookups');
  const suffix = getCustomSuffix();
  const knownKeys = [
    `Case${suffix}`, `Client${suffix}`, `Contact${suffix}`, `Opportunity${suffix}`,
    `Account${suffix}`, `Contract${suffix}`, `Task${suffix}`, `Event${suffix}`,
    `Lead${suffix}`, `Note${suffix}`, `Report${suffix}`,
    `CSAudit${suffix}`, `PAWRecord${suffix}`, `CyberSweepClient${suffix}`,
    `AppConfig${suffix}`, `Activity${suffix}`,
  ];

  const discovered: CustomObject[] = [];
  for (const key of knownKeys) {
    try {
      const type = await retryWithBackoff(
        () => objectTypesApi.getObjectTypeByKey(key, { maxFields: 0 }),
        { maxRetries: 1, initialDelayMs: 1000 }
      );
      if (type) {
        discovered.push({ ...convertFromGobodoObjectType(type), category: 'custom' as const });
      }
    } catch {
      // Type doesn't exist or backend error — skip it
    }
  }

  if (discovered.length > 0) {
    console.log(`[getGobodoObjectTypes] Discovered ${discovered.length} types via individual lookups`);
  }

  return discovered;
}

// ============================================
// FIELD OPERATIONS
// ============================================

/**
 * Create a field for an object type using key
 */
export async function createGobodoField(
  objectKey: string,
  fieldData: Omit<CustomField, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'isStandard'>
): Promise<CustomField> {
  try {
    // Ensure object type exists before creating field
    await ensureObjectTypeExists(objectKey, {
      apiName: objectKey,
      label: objectKey.replace(/__c$/, '').replace(/_/g, ' '),
      pluralLabel: `${objectKey.replace(/__c$/, '').replace(/_/g, ' ')}s`,
      description: `Auto-created object type: ${objectKey}`,
      icon: 'Package',
      category: 'custom',
    });
    
    const client = getGobodoClient();
    
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
    const objectType = await retryWithBackoff(
      () => objectTypesApi.getObjectTypeByKey(objectKey, { maxFields: 0 }),
      { maxRetries: 2 }
    );
    
    const fieldsApi = new GobodoApi.FieldsApi(client);
    const gobodoField = convertToGobodoField(fieldData);
    gobodoField.objectTypeId = objectType.id;
    
    // For relationship fields, resolve target object type ID and set objectSettings.objecttypeid
    if ((fieldData.fieldType === 'lookup' || fieldData.fieldType === 'masterdetail') && fieldData.lookupObject) {
      try {
        const targetKey = normalizeToGobodoFormat(fieldData.lookupObject);
        const targetObjectType = await retryWithBackoff(
          () => objectTypesApi.getObjectTypeByKey(targetKey, { maxFields: 0 }),
          { maxRetries: 2 }
        );
        if (targetObjectType?.id != null && targetObjectType.id !== 0) {
          const os = (gobodoField as any).objectSettings;
          if (os && typeof os === 'object') {
            os.objecttypeid = targetObjectType.id;
          }
        }
      } catch (err) {
        console.warn('[createGobodoField] Could not resolve target object type for objectSettings.objecttypeid:', fieldData.lookupObject, err);
      }
    }
    
    const response = await retryWithBackoff(
      () => fieldsApi.createObjectTypeField(objectType.id, gobodoField),
      { maxRetries: 2 }
    );
    
    return convertFromGobodoField(response, objectKey);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'createGobodoField', objectType: objectKey, fieldName: fieldData.fieldName });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/** Page size for listing all fields when building record payloads (avoids many tiny /fields requests). */
export const GOBODO_FIELDS_PAGE_SIZE_FULL = 200;

/**
 * Get fields for an object type using paginated endpoint
 * @param objectKey - Object type key
 * @param options - Pagination options; pass `objectTypeId` to skip `getObjectTypeByKey` when already known (fewer HTTP calls during batch creates)
 * @returns Object with fields array, hasMore flag, and page info
 */
export async function getGobodoFields(
  objectKey: string,
  options?: { page?: number; size?: number; objectTypeId?: number }
): Promise<{
  fields: CustomField[];
  hasMore: boolean;
  page: number;
  size: number;
}> {
  try {
    const suffix = getCustomSuffix();
    const normalizedKey = objectKey.includes(suffix)
      ? objectKey
      : normalizeToGobodoFormat(objectKey);
    
    const client = getGobodoClient();
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
    
    let resolvedTypeId = options?.objectTypeId;
    if (resolvedTypeId == null || resolvedTypeId === 0) {
      const objectType = await retryWithBackoff(
        () => objectTypesApi.getObjectTypeByKey(normalizedKey, { maxFields: 0 }),
        { maxRetries: 2 }
      );

      if (!objectType || !objectType.id) {
        throw new Error(`Object type not found or has no ID for key: ${normalizedKey}`);
      }
      resolvedTypeId = objectType.id as number;
    }
    
    const page = options?.page ?? 0;
    const size = options?.size ?? 10;
    
    const fieldsResponse = await retryWithBackoff(
      () => objectTypesApi.getObjectTypeFields(resolvedTypeId!, { page, size }),
      { maxRetries: 2 }
    );
    
    const rawFieldCount = (fieldsResponse || []).length;
    const fields = (fieldsResponse || []).map((field: any) => 
      convertFromGobodoField(field, normalizedKey)
    );
    
    const fieldsWithLookupObject = await enrichRelationshipFields(fields, client);
    
    const hasMore = rawFieldCount >= size;
    
    return {
      fields: fieldsWithLookupObject,
      hasMore,
      page,
      size
    };
  } catch (error: any) {
    const normalizedKeyForError = normalizeToGobodoFormat(objectKey);
    const gobodoError = handleGobodoError(error, { operation: 'getGobodoFields', objectType: normalizedKeyForError });
    if (gobodoError) {
      const isNotFound = error?.response?.status === 404;
      logGobodoError(gobodoError, isNotFound ? 'warn' : 'error');
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Load every field for an object type (paginated) for record create/update coercion.
 * Uses {@link GOBODO_FIELDS_PAGE_SIZE_FULL} per page and skips `getObjectTypeByKey` when `objectTypeId` is provided.
 */
export async function getAllGobodoFields(
  objectKey: string,
  options?: { objectTypeId?: number }
): Promise<CustomField[]> {
  const pageSize = GOBODO_FIELDS_PAGE_SIZE_FULL;
  const allFields: CustomField[] = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const result = await getGobodoFields(objectKey, {
      page,
      size: pageSize,
      objectTypeId: options?.objectTypeId,
    });
    allFields.push(...result.fields);
    hasMore = result.hasMore && result.fields.length > 0;
    page += 1;
  }

  return allFields;
}

/**
 * Resolve object key for a field. When the API returns objectTypeId 0 (e.g. for some relationship
 * fields), use the provided objectId so we don't call getObjectTypeById(0).
 */
async function getObjectKeyForField(
  existingField: { objectTypeId?: number },
  objectTypesApi: GobodoApi.GraphObjectTypesApi,
  objectId?: string
): Promise<string> {
  const typeId = existingField.objectTypeId;
  if (typeId != null && typeId !== 0) {
    const objectType = await retryWithBackoff(
      () => objectTypesApi.getObjectTypeById(typeId),
      { maxRetries: 2 }
    );
    return objectType.key || objectType.name;
  }
  if (objectId) {
    return objectIdToKey(objectId);
  }
  throw new Error(
    'Cannot determine object type for field: API returned objectTypeId 0. Please pass the object id when updating this field.'
  );
}

/**
 * Update a field using field ID (will fetch object type from field, or use objectId when API returns objectTypeId 0)
 */
export async function updateGobodoField(
  fieldId: number,
  updates: Partial<CustomField>,
  objectId?: string
): Promise<CustomField> {
  try {
    const client = getGobodoClient();
    const fieldsApi = new GobodoApi.FieldsApi(client);
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

    const existingField = await retryWithBackoff(
      () => fieldsApi.getFieldById(fieldId),
      { maxRetries: 2 }
    );

    const objectKey = await getObjectKeyForField(existingField, objectTypesApi, objectId);

    const updatedFieldData: Partial<CustomField> = {
      ...convertFromGobodoField(existingField, objectKey),
      ...updates
    };

    const gobodoField = convertToGobodoField(updatedFieldData as any);
    if (existingField.objectTypeId != null && existingField.objectTypeId !== 0) {
      gobodoField.objectTypeId = existingField.objectTypeId;
    }

    const response = await retryWithBackoff(
      () => fieldsApi.updateField(fieldId, gobodoField),
      { maxRetries: 2 }
    );
    
    // Handle 204 response - fetch updated field if response is empty
    if (!response || (response as any).status === 204) {
      const updatedField = await retryWithBackoff(
        () => fieldsApi.getFieldById(fieldId),
        { maxRetries: 2 }
      );
      return convertFromGobodoField(updatedField, objectKey);
    }
    
    return convertFromGobodoField(response, objectKey);
  } catch (error: any) {
    if (error.response?.status === 204 || error.status === 204) {
      try {
        const client = getGobodoClient();
        const fieldsApi = new GobodoApi.FieldsApi(client);
        const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
        const existingField = await fieldsApi.getFieldById(fieldId);
        const objectKey = await getObjectKeyForField(existingField, objectTypesApi, objectId);
        return convertFromGobodoField(existingField, objectKey);
      } catch (fetchError: any) {
        const gobodoError = handleGobodoError(fetchError, { operation: 'updateGobodoField', fieldId });
        if (gobodoError) {
          logGobodoError(gobodoError);
          throw gobodoError;
        }
        throw ensureError(fetchError);
      }
    }
    if (error?.response?.status >= 500) {
      const body = error?.response?.data ?? error?.body ?? (error?.message && String(error.message).startsWith('<!') ? error.message : null);
      const preview = body != null
        ? (typeof body === 'string' ? body.slice(0, 500) : JSON.stringify(body).slice(0, 500))
        : (error?.response ? `status ${(error.response as any).status}` : 'no body');
      console.error('[Gobodo] Field update 5xx (fieldId=%s): %s', fieldId, preview);
    }
    const gobodoError = handleGobodoError(error, { operation: 'updateGobodoField', fieldId });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}

/**
 * Delete a field using the deleteField API.
 * Returns the objectId for cache invalidation.
 * Pass objectId when the API returns objectTypeId 0 for this field.
 */
export async function deleteGobodoField(fieldId: number, objectId?: string): Promise<string> {
  try {
    const client = getGobodoClient();
    const fieldsApi = new GobodoApi.FieldsApi(client);
    const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

    const existingField = await retryWithBackoff(
      () => fieldsApi.getFieldById(fieldId),
      { maxRetries: 2 }
    );

    const objectKey = await getObjectKeyForField(existingField, objectTypesApi, objectId);

    const tryDelete = (): Promise<boolean> => {
      return (client as any).callApi(
        '/fields/{fieldid}',
        'DELETE',
        { fieldid: fieldId },
        {},
        {},
        {},
        null,
        ['NexusGraphAPIAuth', 'NexusGraphAPIKey'],
        [],
        ['*/*'],
        null,
        null
      ).then(() => true).catch((e: any) => {
        const status = e?.response?.status ?? e?.status;
        if (status === 404 || status === 405) return false;
        throw e;
      });
    };

    const deleted = await retryWithBackoff(() => tryDelete(), { maxRetries: 2 });
    if (deleted) return keyToObjectId(objectKey);

    const asCustom = convertFromGobodoField(existingField, objectKey);
    const softDeletePayload = convertToGobodoField({ ...asCustom, isActive: false } as any);
    if (existingField.objectTypeId != null && existingField.objectTypeId !== 0) {
      softDeletePayload.objectTypeId = existingField.objectTypeId;
    }
    await retryWithBackoff(
      () => fieldsApi.updateField(fieldId, softDeletePayload),
      { maxRetries: 2 }
    );
    return keyToObjectId(objectKey);
  } catch (error: any) {
    if (error.response?.status === 204 || error.status === 204) {
      try {
        const client = getGobodoClient();
        const fieldsApi = new GobodoApi.FieldsApi(client);
        const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
        const existingField = await fieldsApi.getFieldById(fieldId);
        const objectKey = await getObjectKeyForField(existingField, objectTypesApi, objectId);
        return keyToObjectId(objectKey);
      } catch (fetchError: any) {
        return objectId || '';
      }
    }
    const gobodoError = handleGobodoError(error, { operation: 'deleteGobodoField', fieldId });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw ensureError(error);
  }
}
